import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { notifications } from "@mantine/notifications";
import type { Role } from "@wwrp/shared";
import { authApi, type AuthUser, LOGIN_REDIRECT_KEY } from "../api/auth.js";

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refetch: () => Promise<void>;
  logout: () => Promise<void>;
  setViewAsRole: (role: Role) => Promise<void>;
  clearViewAsRole: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setUser(await authApi.me());
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
      notifications.show({ title: "Goodbye!", message: "You have successfully logged out" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to log out";
      notifications.show({ message, color: "red" });
    } finally {
      setUser(null);
    }
  }, []);

  const setViewAsRole = useCallback(
    async (role: Role) => {
      await authApi.setViewAsRole(role);
      await refetch();
    },
    [refetch],
  );

  const clearViewAsRole = useCallback(async () => {
    await authApi.clearViewAsRole();
    await refetch();
  }, [refetch]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (loading) return;
    const shouldNotify = (() => {
      try {
        return sessionStorage.getItem(LOGIN_REDIRECT_KEY) === "1";
      } catch {
        return false;
      }
    })();

    if (!shouldNotify) return;

    try {
      sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
    } catch {
      // Ignore storage errors
    }

    if (user) {
      notifications.show({ title: "Welcome back!", message: "You have successfully logged in" });
    } else {
      notifications.show({ message: "Login failed. Please try again.", color: "red" });
    }
  }, [loading, user]);

  const value = useMemo(
    () => ({ user, loading, refetch, logout, setViewAsRole, clearViewAsRole }),
    [user, loading, refetch, logout, setViewAsRole, clearViewAsRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
